import LBox from "@components/LBox";
import LScreen from "@components/LScreen";
import BottomBarPadding from "@components/BottomBarPadding";
import { useLibrary } from "@hooks/trpc/useLibrary";
import { theme } from "@theme";
import { keyExtractor } from "@utils/data";
import { useEffect, useMemo, useState, useRef } from "react";
import { Keyboard, Platform, RefreshControl } from "react-native";
import { FlashListRef } from "@shopify/flash-list";
import LibraryItem from "@components/LibraryItem";
import {
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";
import { SentryScreenDisplay } from "@components/SentryScreenDisplay";

import { PROFILE_ROUTES } from "@components/Account/Layout/utils";
import Header from "@components/Account/Layout/Header/Header";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import { useOfflineLibrary } from "@utils/downloadManager/hooks";

import { useAuth } from "@utils/firebaseAuth/auth";
import NotLoggedIn from "@components/Account/NotLoggedIn";
import PlaylistSyncProgress from "@components/PlaylistSyncProgress";
import useInternetStatus from "@utils/internetStatus";

import { Filter } from "./types";
import AnimatedFlashListCellRenderer from "./AnimatedFlashListCellRenderer";
import ReanimatedFlashList from "./ReanimatedFlashList";
import CreatePlaylistButton from "./CreatePlaylistButton";
import LibraryFilterBar from "./LibraryFilterBar";
import LibraryEmptyState, { libraryListState } from "./LibraryEmptyState";

const LibraryScreen = () => {
  const {
    query: {
      data: library,
      isFetching,
      isPending,
      isFetched,
      isError,
      refetch,
    },
  } = useLibrary();

  const { status: authStatus } = useAuth();
  const currentTab = useNavigationStore((state) => state.tab);

  const [filter, setFilterState] = useState<Filter | null>(null);
  // Online status is read from the external internet-status store via
  // useSyncExternalStore, so it never needs to be re-synced with setState in an
  // effect (which removes the render<->subscribe race entirely).
  const isOnline = useInternetStatus();
  const [wasDownloadsAutoSelected, setWasDownloadsAutoSelected] =
    useState(false);
  const [wasDownloadsManuallyDeselected, setWasDownloadsManuallyDeselected] =
    useState(false);
  const isLoggedIn = authStatus === "logged-in";
  const list = useRef<FlashListRef<any>>(null);

  // React to online/offline changes during render (covers both transitions and
  // the already-offline-on-mount case) instead of subscribing in an effect.
  // Each set is guarded on an actual change so this stays a stable, loop-free
  // render-time derivation. The imperative scrollTo side effect is handled in
  // the effect below, keyed on `filter`.
  if (
    !isOnline &&
    isLoggedIn &&
    !wasDownloadsManuallyDeselected &&
    filter !== "downloads"
  ) {
    setFilterState("downloads");
  }
  if (
    !isOnline &&
    isLoggedIn &&
    !wasDownloadsManuallyDeselected &&
    !wasDownloadsAutoSelected
  ) {
    setWasDownloadsAutoSelected(true);
  }
  if (
    isOnline &&
    isLoggedIn &&
    wasDownloadsAutoSelected &&
    filter === "downloads"
  ) {
    setFilterState(null);
    setWasDownloadsAutoSelected(false);
  }
  if (isOnline && wasDownloadsManuallyDeselected) {
    setWasDownloadsManuallyDeselected(false);
  }

  const setFilter = (newFilter: Filter | null) => {
    if (isLoggedIn) {
      // If user manually deselects downloads while offline, mark it
      if (
        !isOnline &&
        filter === "downloads" &&
        newFilter === null &&
        wasDownloadsAutoSelected
      ) {
        setWasDownloadsManuallyDeselected(true);
      }
      // Clear auto-selected flag when user manually changes filter
      if (wasDownloadsAutoSelected) {
        setWasDownloadsAutoSelected(false);
      }
      setFilterState(newFilter);
      // Scrolling the filter bar back to the start is handled by the effect
      // keyed on `filter`.
    }
  };

  //sort library into albums, playlists and artists, with a single pass
  const { playlists, albums, artists } = useMemo(() => {
    if (!library)
      return {
        playlists: [],
        albums: [],
        artists: [],
      };

    const albums: {
      id: string;
      type: "album";
      title: string;
      image: string | null;
      artistDisplayText: string;
      privateSongIds: string[];
    }[] = [];
    const artists: {
      id: string;
      type: "artist";
      name: string;
      image: string | null;
    }[] = [];
    const playlists: {
      id: string;
      type: "playlist";
      title: string;
      images: string[];
      songIds: string[];
      user: {
        id: string;
        name: string;
      };
      visibility: "public" | "private";
      privateSongIds: string[];
    }[] = [];

    library.forEach((item) => {
      switch (item.type) {
        case "playlist": {
          playlists.push({
            ...item,
            images: item.images ?? [],
          });
          break;
        }
        case "album":
          albums.push(item);
          break;
        case "artist":
          artists.push(item);
          break;
      }
    });

    return { playlists, albums, artists };
  }, [library]);

  const { offlineLibrary } = useOfflineLibrary();

  const activeItems = useMemo(() => {
    switch (filter) {
      case "playlist":
        return playlists;
      case "album":
        return albums;
      case "artist":
        return artists;
      case "media":
        return [];
      case "downloads":
        return offlineLibrary?.map((item) => {
          if (item.type === "playlist") {
            return {
              ...item,
              images: item.images ?? [],
              songIds: item.songs.map((song) => song.id),
              privateSongIds: [],
            };
          }
          if (item.type === "album") {
            return {
              ...item,
              privateSongIds: [],
            };
          }
          return item;
        });
      case null:
        return library;
    }
  }, [albums, artists, filter, library, playlists, offlineLibrary]);

  // Downloads come from the device and never wait for the online library.
  const listItems = useMemo(() => activeItems ?? [], [activeItems]);

  const y = useSharedValue(0);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll(event) {
      y.value = event.contentOffset.y;
    },
  });
  const listScrollHandler = Platform.OS === "web" ? undefined : scrollHandler;

  const [keyboardOpen, setKeyboardOpen] = useState<boolean>(false);
  useEffect(() => {
    const showListener = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      () => setKeyboardOpen(true),
    );
    const hideListener = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => setKeyboardOpen(false),
    );
    return () => {
      showListener.remove();
      hideListener.remove();
    };
  }, []);

  const showCreatePlaylistButton =
    !keyboardOpen && (!filter || filter === "playlist");

  const isLoading = isFetching || isPending;
  const isReady = !isError && !isLoading;
  const listState = libraryListState({
    filter,
    offlineLibraryRead: offlineLibrary !== undefined,
    hasLibrary: library !== undefined,
    isOnline,
    isFetching,
    isError,
  });

  console.log(
    `::Library:: data: ${library?.length}, isReady: ${isReady}, isFetching: ${isFetching}, isPending: ${isPending}, isFetched: ${isFetched}, isError: ${isError}`,
  );

  return (
    <LScreen
      enableAutomaticTopPadding={true}
      enableAutomaticBottomPadding={!isLoggedIn}
      keyboard
    >
      <SentryScreenDisplay ready={!isLoggedIn || isReady} />
      {currentTab === "profile" && (
        <LBox pb="s4">
          <Header routes={PROFILE_ROUTES} />
        </LBox>
      )}
      <LBox flex={1} justifyContent="flex-start">
        <LibraryFilterBar filter={filter} onFilterChange={setFilter} />

        {isLoggedIn ? (
          <>
            <PlaylistSyncProgress />
            <ReanimatedFlashList
              ref={list}
              maintainVisibleContentPosition={{ disabled: true }}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              data={listItems as any}
              keyExtractor={(item: any) => keyExtractor(item)}
              renderItem={({ item }: { item: any }) => (
                <LibraryItem data={item} />
              )}
              CellRendererComponent={AnimatedFlashListCellRenderer}
              contentContainerStyle={{
                paddingTop: theme.spacing.s4,
              }}
              onScroll={listScrollHandler}
              scrollEventThrottle={16}
              onScrollEndDrag={listScrollHandler}
              bounces={true}
              alwaysBounceVertical={true}
              refreshControl={
                <RefreshControl
                  refreshing={false}
                  onRefresh={async () => {
                    console.log(
                      "::Library:: Pull to refresh triggered - forcing fresh API fetch",
                    );
                    try {
                      // Also trigger the regular refetch to update the UI
                      await refetch();
                    } catch (error) {
                      console.error(
                        "::Library:: Error during pull to refresh:",
                        error,
                      );
                      // Fallback to regular refetch if forced fetch fails
                      refetch().catch(console.error);
                    }
                  }}
                  tintColor={theme.colors.white}
                  colors={[theme.colors.white]}
                />
              }
              ListFooterComponent={
                keyboardOpen ? undefined : <BottomBarPadding />
              }
              ListEmptyComponent={
                <LibraryEmptyState
                  state={listState}
                  filter={filter}
                  onShowDownloads={() => setFilter("downloads")}
                  onRetry={() => {
                    refetch().catch(console.error);
                  }}
                />
              }
            />
            {showCreatePlaylistButton ? <CreatePlaylistButton y={y} /> : null}
          </>
        ) : (
          <LBox flex={1} alignItems="center" justifyContent="center">
            <NotLoggedIn
              flex={1}
              alignItems="center"
              justifyContent="center"
              width="70%"
              header="Access your Library"
              text="Log in to see your saved playlists, favourites, and listening history."
            />
          </LBox>
        )}
      </LBox>
    </LScreen>
  );
};

export default LibraryScreen;
